/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { fileService } from 'src/core/services/api/fileService';
import { getAzureContainerService } from 'src/core/services/azure/azureContainerService';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { getStorageUrl } from 'src/core/utils/api/storageUtils';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { TargetFile } from 'src/languageStudio/core/hooks/useTranslatorCapabilities';
import { getTranslationService } from 'src/languageStudio/core/services/translation/translationService';
import { InputDocument, TranslationDocumentSwagger } from 'src/languageStudio/core/services/translation/translatorSwagger';
import { mapTranslationDocumentsSwagger, mapTranslationSwagger } from 'src/translation/handlers/mappers/translationHandlerGroupMappers';

export type TranslationHandlerGroup = ReturnType<typeof createTranslationHandlerGroup>;
const getGlossaryFormat = (glossaryExtension: string) => {
    switch (glossaryExtension) {
        case 'xlf':
            return 'xliff';
        default:
            return glossaryExtension;
    }
};
const getTargetContainerPath = (storageAccountName: string, containerName: string, folderName: string, fileName?: string) => {
    let path = getStorageUrl({ storageAccountName, containerName });

    if (folderName) {
        path = path.concat(`/${folderName}`);
    }

    if (fileName) {
        path = path.concat(`/${fileName}`);
    }

    return path;
};

const addDocNameToTarget = (document: InputDocument) => ({
    ...document,
    targets: document.targets.map(t => ({
        ...t,
        targetUrl: `${t.targetUrl}/${t.language}-${document.name}`
    }))
});

const addSourceLanguageToSource = (document: InputDocument, sourceLanguage: string) => ({
    ...document,
    source: { ...document.source, language: sourceLanguage }
});

export const createTranslationHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    const getUploadingFolderName = () => {
        const timeStamp = new Date().toISOString();
        const userAlias = rootStore.userStore.email.split('@')[0];

        return `translated/${userAlias}/${timeStamp}`;
    };

    const translateDocuments = async ({
        files,
        tracker,
        isAllFiles,
        glossaryFiles,
        sourceLanguage,
        targetLanguages,
        resourceGroupName,
        destinationFolder,
        storageAccountName,
        downloadTranslatedFiles,
        customTranslatorModelId,
        destinationContainerName
    }: HandlerParams<{
        files: TargetFile[];
        isAllFiles: boolean;
        sourceLanguage: string;
        targetLanguages: string[];
        destinationFolder: string;
        resourceGroupName: string;
        storageAccountName: string;
        glossaryFiles?: TargetFile[];
        customTranslatorModelId: string;
        downloadTranslatedFiles: boolean;
        destinationContainerName: string;
    }>) => {
        const azureResource = rootStore.azureStore.session.csAccountStore;
        // Check the selected target container if there is none with
        // download option active then create one on the fly
        const targetContainerName = destinationContainerName ?? LuisConstants.TranslationTool.AutoGeneratedContainerName;
        const targetFolderName = destinationFolder || getUploadingFolderName();

        if (!destinationContainerName) {
            await getAzureContainerService().createAzureContainer({
                resourceGroupName,
                storageAccountName,
                containerName: targetContainerName,
                token: rootStore.azureStore.session.userTokens.armToken,
                subscriptionId: azureResource.subscriptionId
            });
        }

        // Start preparing the documents.
        let targets: InputDocument['targets'] = targetLanguages.map(t => ({
            language: t,
            targetUrl: getTargetContainerPath(storageAccountName, targetContainerName, targetFolderName),
            glossaries: glossaryFiles
                ?.filter(f => f.languageCode === t)
                .map(g => {
                    const glossaryFileNameComponents = g.name.split('.');

                    return {
                        format: getGlossaryFormat(glossaryFileNameComponents[glossaryFileNameComponents.length - 1]),
                        glossaryUrl: getStorageUrl({ storageAccountName, containerName: g.containerName, blobName: g.name })
                    };
                })
        }));

        if (customTranslatorModelId) {
            targets = targets.map(t => ({
                ...t,
                category: customTranslatorModelId
            }));
        }

        let documents: InputDocument[] = isAllFiles
            ? [
                  {
                      targets,
                      name: files[0].containerName,
                      source: { sourceUrl: getStorageUrl({ storageAccountName, containerName: files[0].containerName }) }
                  }
              ]
            : files.map<InputDocument>(f => {
                  const filePath = f.name.split('/');
                  return {
                      targets,
                      name: filePath[filePath.length - 1],
                      storageType: 'File',
                      source: {
                          sourceUrl: getStorageUrl({ storageAccountName, containerName: f.containerName, blobName: f.name })
                      }
                  };
              });

        if (sourceLanguage !== LuisConstants.TranslationTool.AutoLanguageDetectionItemKey) {
            documents = documents.map(doc => addSourceLanguageToSource(doc, sourceLanguage));
        }
        let startTranslationOperationUrl = '';
        startTranslationOperationUrl = await getTranslationService().translateDocuments({
            documents: documents.map(addDocNameToTarget),
            resourceName: azureResource.name
        });
        tracker.setState('completed');

        let filesToDownload: TranslationDocumentSwagger[];

        if (downloadTranslatedFiles) {
            await getTranslationService().fetchDocumentsByOperationUrl({
                startTranslationOperationUrl,
                handler: {
                    timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                    handleCall: res => {
                        filesToDownload = res.value;
                    },
                    retryStrategy: res =>
                        res.data.value.reduce(
                            (acc, document) => acc || document.status === 'NotStarted' || document.status === 'Running',
                            false
                        )
                },
                resourceName: azureResource.name
            });

            // Download files.
            await Promise.all([
                ...filesToDownload.map(d => {
                    const sourceUrlTokens = d.path.split('/');
                    const fileName = sourceUrlTokens[sourceUrlTokens.length - 1];

                    handleDownloadFile(fileName, d.path);
                })
            ]);
        }
    };

    /**
     * This method is used to download a file from blob store.
     */
    const downloadFile = async ({
        fileName,
        storageAccountName,
        containerName,
        folderName = null
    }: HandlerParams<{ fileName: string; storageAccountName: string; containerName: string; folderName?: string }>) => {
        const path = getTargetContainerPath(storageAccountName, containerName, folderName, fileName);

        await handleDownloadFile(fileName, path);
    };

    const handleDownloadFile = async (fileName: string, path: string) => {
        const options = luisRequestOptionsBuilder()
            .useContentType('xml')
            .useArmToken(rootStore.azureStore.session.userTokens.storageToken)
            .skipThrottling()
            .build();
        options.headers['x-ms-version'] = '2017-11-09';

        await fileService.download(path, fileName, options);
    };

    /**
     * This method is used to cancel a translation job.
     */
    const cancelTranslationJob = async ({ jobId }: HandlerParams<{ jobId: string }>) => {
        const azureResource = rootStore.azureStore.session.csAccountStore;
        const translationJobsStore = rootStore.translationJobStoreHouse;
        translationJobsStore.findById(jobId).documentStoreHouse.tracker.setState('running');
        await getTranslationService().cancelTranslation({
            jobId,
            resourceName: azureResource.name
        });

        getTranslationService().getTranslationDocumentsStatus({
            id: jobId,
            handler: {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                handleCall: res => {
                    rootStore.translationJobStoreHouse.findById(jobId).documentStoreHouse.tracker.setState('completed');
                    res.value.forEach(job => {
                        const currentJob = rootStore.translationJobStoreHouse.findById(jobId).documentStoreHouse.findById(job.id);
                        if (!currentJob) {
                            rootStore.translationJobStoreHouse.findById(jobId).documentStoreHouse.add(mapTranslationDocumentsSwagger(job));
                        } else {
                            currentJob.setStatus(job.status);
                        }
                    });
                },
                retryStrategy: res =>
                    res.data.value.reduce(
                        (acc, job) => acc || job.status === 'Running' || job.status === 'NotStarted' || job.status === 'Cancelling',
                        false
                    )
            },
            resourceName: azureResource.name
        });
    };

    /**
     * This method is used to fetch the translation jobs for a specific resource.
     */
    const fetchTranslationJobs = async ({}: HandlerParams<{}>) => {
        const azureResource = rootStore.azureStore.session.csAccountStore;
        const translationJobsStore = rootStore.translationJobStoreHouse;
        translationJobsStore.tracker.setState('running');

        await getTranslationService().pollTranslationsStatus({
            handler: {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                handleCall: res => {
                    res.value.forEach(job => {
                        const currentJob = translationJobsStore.findById(job.id);
                        if (!currentJob) {
                            translationJobsStore.add(mapTranslationSwagger(job));
                        } else {
                            currentJob.setStatus(job.status);
                            currentJob.setLastActionDateTimeUtc(new Date(job.lastActionDateTimeUtc));
                            currentJob.setError(job.error?.message);
                        }
                    });
                    translationJobsStore.tracker.setState('completed');
                },
                retryStrategy: res =>
                    res.data.value.reduce(
                        (acc, job) => acc || job.status === 'Running' || job.status === 'NotStarted' || job.status === 'Cancelling',
                        false
                    )
            },
            resourceName: azureResource.name
        });
    };

    /**
     * This method is used to fetch the documents for a specific jobs list and update the status for the document store until the job has completed.
     */
    const fetchTranslationDocumentsJobs = async ({ jobIds }: HandlerParams<{ jobIds: string[] }>) => {
        if (!jobIds.length) {
            return;
        }
        const azureResource = rootStore.azureStore.session.csAccountStore;

        jobIds.forEach(jobId => rootStore.translationJobStoreHouse.findById(jobId).documentStoreHouse.tracker.setState('running'));

        // wait to fetch all the documents for all the jobs
        await Promise.all(
            jobIds.map(jobId =>
                getTranslationService().getTranslationDocumentsStatus({
                    id: jobId,
                    handler: {
                        timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                        handleCall: res => {
                            const { documentStoreHouse, status: jobStatus } = rootStore.translationJobStoreHouse.findById(jobId);
                            res.value.forEach(document => {
                                const currentDoc = documentStoreHouse.findById(document.id);
                                if (!currentDoc) {
                                    documentStoreHouse.add(mapTranslationDocumentsSwagger(document));
                                } else {
                                    currentDoc.setStatus(document.status);
                                }
                            });
                            documentStoreHouse.tracker.setState(jobStatus === 'NotStarted' ? 'notStarted' : 'completed');
                        },
                        retryStrategy: res =>
                            res.data.value.reduce(
                                (acc, job) => acc || job.status === 'Running' || job.status === 'NotStarted' || job.status === 'Cancelling',
                                false
                            ) ||
                            (!res.data.value.length && rootStore.translationJobStoreHouse.findById(jobId).status === 'NotStarted')
                    },
                    resourceName: azureResource.name
                })
            )
        );
    };

    return {
        downloadFile,
        translateDocuments,
        cancelTranslationJob,
        fetchTranslationJobs,
        fetchTranslationDocumentsJobs
    };
};
